<template>
    <div class="site-detail">
        <!-- Site Header -->
        <detail-page-header
            :title="site.building.address_line_1"
            :subtitle="`${site.building.city}, ${site.building.state}`"
            :show-back-button="!isModal && !fromReatlas"
            :title-clickable="!isModal && !fromReatlas"
            :show-title-chevron="true"
            :badges="headerBadges"
            :show-close-button="isModal || fromReatlas"
            :is-modal="isModal || fromReatlas"
            :draggable="isModal"
            back-button-label="Go back to sites list"
            title-click-label="View all sites"
            close-button-label="Close panel"
            @back="goBack"
            @title-click="showSites"
            @close="$emit('reatlas-close-right-panel')"
        >
            <template #actions>
                <a 
                    v-if="fromReatlas" 
                    :href="site.view_url" 
                    class="site-detail__reatlas-link"
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    Go to Site Page
                </a>

                <themed-dropdown 
                    v-if="!isModal" 
                    label="Manage Site"
                    :actions="manageSiteActions"
                    @action="handleManageSiteAction"
                />
            </template>
        </detail-page-header>

        <!-- Site Content -->
        <div 
            id="site-container" 
            class="site-detail__content" 
            :class="{ 'site-detail__content--modal': isModal || fromReatlas }"
        >
            <!-- Media Gallery and Map Section -->
                <div class="site-detail__media-section">
                    <div class="site-detail__gallery">
                        <media-gallery-v1
                            :documentable-id="site.id"
                            documentable-type="App\Site"
                            :media="site.media"
                            :height="500"
                            :object-cover="!fromReatlas"
                        />
                    </div>
                    
                    <div v-if="!fromReatlas" class="site-detail__map">
                        <map-component
                            :latitude="site.building.latitude"
                            :longitude="site.building.longitude"
                            :zoom="6"
                        />
                    </div>
                    
                    <div v-if="fromReatlas" class="site-detail__quick-facts">
                        <h2 class="site-detail__quick-facts-title">Quick Facts</h2>
                        <themed-table
                            :data="quickFactsData"
                            :columns="quickFactsColumns"
                            :hoverable="false"
                            aria-label="Site Quick Facts"
                        >
                            <!-- Custom rendering for Due in value -->
                            <template #column-value="{ row }">
                                <span v-if="row.field === 'due_in'">
                                    <span v-if="site.lease_or_own == 'Owned'">Owned</span>
                                    <span v-else-if="site.leases.length && site.due_in_days > 0">
                                        Due in <span class="site-detail__fact-highlight">{{ site.due_in_text }}</span>
                                    </span>
                                    <span v-else-if="site.leases.length && site.due_in_days == 0">Expired</span>
                                    <span v-else>No Lease Information</span>
                                </span>
                                <span v-else>{{ row.value }}</span>
                            </template>
                        </themed-table>
                    </div>
                </div>

                <!-- Cost Metrics Section -->
                <div class="site-detail__metrics">
                    <div class="site-detail__metric">
                        <div class="site-detail__metric-value" :data-originalvalue="site.lease_cost">
                            {{ formatCurrency(site.lease_cost, 0) }}
                        </div>
                        <div class="site-detail__metric-label">Total Cost</div>
                    </div>
                    <div v-if="!fromReatlas" class="site-detail__metric">
                        <div class="site-detail__metric-value" :data-originalvalue="site.remaining_lease_cost">
                            {{ formatCurrency(site.remaining_lease_cost, 0) }}
                        </div>
                        <div class="site-detail__metric-label">Remaining Cost</div>
                    </div>
                    <div class="site-detail__metric">
                        <div class="site-detail__metric-value" :data-originalvalue="site.base_psf">
                            {{ formatCurrency(site.base_psf, 2) }}
                        </div>
                        <div class="site-detail__metric-label">Base PSF</div>
                    </div>
                    <div class="site-detail__metric">
                        <div class="site-detail__metric-value" :data-originalvalue="site.current_psf">
                            {{ formatCurrency(site.current_psf, 2) }}
                        </div>
                        <div class="site-detail__metric-label">Current PSF</div>
                    </div>
                    <div class="site-detail__metric">
                        <div class="site-detail__metric-value" :data-originalvalue="site.current_annual_cost">
                            {{ formatCurrency(site.current_annual_cost, 0) }}
                        </div>
                        <div class="site-detail__metric-label">Current Annual</div>
                    </div>
                </div>

                <!-- Tabs Section -->
                <div class="site-detail__tabs-wrapper">
                    <div v-if="isModal || fromReatlas" class="site-detail__tab-select">
                        <label for="section-select" class="site-detail__tab-select-label">Viewing Section</label>
                        <select 
                            id="section-select"
                            v-model="selectedTab" 
                            class="site-detail__tab-select-input"
                            aria-label="Select section to view"
                        >
                            <option 
                                v-for="(tab, slug) in enabledTabs" 
                                :key="slug"
                                :value="slug"
                            >
                                {{ tab.label }}
                            </option>
                        </select>
                    </div>
                    
                    <nav v-if="!fromReatlas" class="site-detail__tabs" aria-label="Site sections">
                        <button
                            v-for="(tab, slug) in enabledTabs"
                            :key="slug"
                            @click="selectedTab = slug"
                            class="site-detail__tab"
                            :class="{ 'site-detail__tab--active': slug === selectedTab, [tab.customClass]: tab.customClass }"
                            :aria-selected="slug === selectedTab"
                            :aria-label="`View ${tab.label} section`"
                            role="tab"
                        >
                            {{ tab.label }}
                            <span
                                v-if="tab.showCount"
                                class="site-detail__tab-count"
                                :class="{ 'site-detail__tab-count--active': slug === selectedTab }"
                            >
                                {{ tab.count }}
                            </span>
                        </button>
                    </nav>
                </div>

                <!-- Tab Content -->
                <div class="site-detail__tab-content" :class="{ 'site-detail__tab-content--bordered': !fromReatlas }">
                    <building-fields 
                        v-if="selectedTab == 'building'" 
                        :building-data="site.building" 
                        :from-reatlas="fromReatlas" 
                        :from-site-id="site.id"
                    />

                    <leases 
                        v-if="selectedTab == 'leases'" 
                        :data="site" 
                        :from-reatlas="fromReatlas"
                        @open-manage-leases="openManageLeases"
                    />

                    <expenses 
                        v-if="selectedTab == 'expenses'" 
                        :data="site.leases" 
                        :site-id="site.id" 
                        :from-reatlas="fromReatlas"
                        @open-manage-leases="openManageLeases"
                    />

                    <site-utilization
                        v-if="selectedTab == 'utilization'"
                        :show-industrial-chart="!fromReatlas"
                        :from-reatlas="fromReatlas"
                    />

                    <key-dates 
                        v-if="selectedTab == 'key_dates'" 
                        :site-id="site.id"
                        :from-reatlas="fromReatlas"
                        @open-manage-leases="openManageLeases"
                    />

                    <task-cards
                        v-if="selectedTab == 'tasks'"
                        :site-id="site.id"
                        title="Tasks"
                        :show-title="true"
                        :searchable="true"
                        create-button-text="New Task"
                        modal-name="site-task-form-modal"
                    />

                    <documents
                        v-if="selectedTab == 'documents'"
                        :document-data="site.documents"
                        :documentable-id="site.id"
                        documentable-type="App\Site"
                        :from-reatlas="fromReatlas"
                    />

                    <contacts
                        v-if="selectedTab == 'contacts'"
                        :contact-data="site.contacts"
                        attach-to="site"
                        :attach-to-id="site.id"
                    />

                    <building-danger-zone
                        v-if="selectedTab == 'dangerZone'"
                        :building-data="site.building"
                        :view-only-mode="true"
                    />

                    <comments
                        v-if="selectedTab == 'comments'"
                        :data="site.comments"
                        :document-data="site.documents"
                        :commentable-id="site.id"
                        commentable-type="App\Site"
                    />

                    <site-imports
                        v-if="selectedTab == 'site_imports'"
                        :site-id="site.id"
                        :initial-customers="site.site_imports || []"
                        @imported="handleSiteImports"
                    />

                    <changelog
                        v-if="selectedTab == 'changelog' && $gate.isAdmin()"
                        resource-type="site"
                        :resource-id="site.id"
                    />
                </div>

        <!-- Modals -->
        <notifications classes="notification" position="bottom right" />

        <modal 
            @opened="$eventHub.$emit('convert-currency')" 
            name="site-cards" 
            width="1300px" 
            height="auto" 
            click-to-close 
            scrollable 
            adaptive
        >
            <div class="site-detail__modal-content">
                <site-cards
                    view="table"
                    :per-page="7"
                    :site-data="sites.all"
                    :show-create-new="false"
                    :show-client-info="false"
                    :show-filters="false"
                    small-header
                />
            </div>
        </modal>

        <manage-leases :selectedSection="leaseSection" />
        </div>
    </div>
</template>

<script>
    import { mapState } from 'vuex';
    import KeyDates from './KeyDates.vue';
    import BuildingFields from './BuildingFields.vue';
    import BuildingDangerZone from './BuildingDangerZone.vue';
    import Leases from '../leases/Leases.vue';
    import ManageLeases from '../leases/ManageLeases.vue';
    import Expenses from '../expenses/Expenses.vue';
    import Documents from '../documents/Documents.vue';
    import Contacts from '../contacts/Contacts.vue';
    import Comments from '../comments/Comments.vue';
    import Changelog from '../changelog/Changelog.vue';
    import ThemedDropdown from '../ThemedDropdown.vue';
    import ThemedTable from '../ThemedTable.vue';
    import DetailPageHeader from '../DetailPageHeader.vue';
    import MediaGalleryV1 from '../MediaGallery.vue';
    import TaskCards from '../tasks/TaskCards.vue';

    export default {
        name: 'ThemedSite',
        
        components: {
            KeyDates,
            BuildingFields,
            ThemedTable,
            BuildingDangerZone,
            Leases,
            ManageLeases,
            Expenses,
            Documents,
            Contacts,
            Comments,
            Changelog,
            ThemedDropdown,
            DetailPageHeader,
            MediaGalleryV1,
            TaskCards
        },
        
        props: {
            data: {
                type: Object,
                required: true
            },

            openTab: {
                type: String,
                default: 'building'
            },

            isModal: {
                type: Boolean,
                default: false
            },

            fromReatlas: {
                type: Boolean,
                default: false
            }
        },

        data() {
            return {
                selectedTab: this.openTab,
                leaseSection: 'lease-info'
            }
        },

        created() {
            this.sites.selectedSite = this.data;
        },

        mounted() {
            this.buildings.selectedBuilding = this.site.building;

            this.leases.all = this.leases.filtered = this.site.leases;
            this.leases.selectedLease = this.site.leases.length ? this.site.leases[0] : null;

            this.contacts.all = this.site.contacts;
            this.contacts.filtered = this.site.contacts;

            this.documents.all = this.site.documents;
            this.documents.filtered = this.site.documents;

            localStorage.setItem(`reopt_baseCurrency`, this.site.currency);

            let selectedCurrency = localStorage.getItem(`reopt_selectedCurrency`) || 'USD';

            if (this.site.currency != selectedCurrency) {
                this.$eventHub.$emit('convert-currency', {
                    from: this.site.currency,
                    to: selectedCurrency
                });
            }

            this.$store.dispatch('loadSites');

            this.$eventHub.$on('site-saved', site => {
                this.site = site;
                this.sites.selectedSite = site;
            });
        },

        watch: {
            data() {
                this.sites.selectedSite = this.data;
                this.leases.all = this.data.leases;
                this.leases.filtered = this.data.leases;
                this.leases.selectedLease = this.data.leases[0] || {};
                this.documents.all = this.data.documents;
                this.documents.filtered = this.data.documents;
            }
        },

        computed: {
            enabledTabs() {
                // v-for used to be paired with v-if="tab.enabled"; Vue 2 runs
                // v-for first so the list was already filtered. Same result, and
                // it does not break when the directive priority reverses in Vue 3.
                const source = this.tabs;
                return Array.isArray(source)
                    ? source.filter(entry => entry.enabled)
                    : Object.fromEntries(Object.entries(source || {}).filter(([, entry]) => entry.enabled));
            },

            ...mapState(['sites', 'buildings', 'documents', 'leases', 'contacts']),

            site: {
                get() {
                    return this.sites.selectedSite;
                },
                set(value) {
                    this.$store.commit('UPDATE_SITE', value);
                }
            },

            headerBadges() {
                const badges = [];
                
                // Utilization badge
                badges.push({
                    text: `${this.site.utilization}%`,
                    class: `site-detail__utilization-badge ${this.site.utilization_color}`,
                    title: `Utilization: ${this.site.utilization}%`,
                    ariaLabel: `Site utilization is ${this.site.utilization} percent`
                });
                
                // Archived badge
                if (this.site.archived_at != null) {
                    badges.push({
                        icon: 'archived',
                        text: 'Archived',
                        class: 'badge-default',
                        role: 'status',
                        ariaLabel: 'Site is archived'
                    });
                }
                
                return badges;
            },

            manageSiteActions() {
                return [
                    {
                        label: 'Edit Building',
                        icon: 'edit-icon',
                        action: 'edit-building',
                        show: this.canEdit() && !this.fromReatlas,
                        ariaLabel: 'Edit building'
                    },
                    {
                        label: 'Leases',
                        icon: 'document-text',
                        action: 'manage-leases',
                        ariaLabel: 'Manage leases'
                    },
                    {
                        label: 'Expenses',
                        icon: 'money',
                        action: 'manage-expenses',
                        ariaLabel: 'Manage expenses'
                    },
                    {
                        label: 'Key Dates',
                        icon: 'calendar',
                        action: 'manage-key-dates',
                        ariaLabel: 'Manage key dates'
                    },
                    {
                        label: 'Archive Site',
                        icon: 'archived',
                        action: 'archive-site',
                        show: this.site.archived_at == null,
                        // Separates the reversible actions above from Delete below.
                        // Only drawn when Delete is actually present, so the menu
                        // never ends on a trailing rule.
                        divider: this.canDelete(),
                        ariaLabel: 'Archive site'
                    },
                    {
                        label: 'Unarchive Site',
                        icon: 'archived',
                        action: 'unarchive-site',
                        show: this.site.archived_at != null,
                        divider: this.canDelete(),
                        ariaLabel: 'Unarchive site'
                    },
                    {
                        label: 'Delete Site',
                        icon: 'trash',
                        action: 'delete-site',
                        show: this.canDelete(),
                        danger: true,
                        ariaLabel: 'Delete site',
                        class: 'site-detail__dropdown-item--danger'
                    }
                ];
            },

            tabs() {
                return {
                    building: {
                        label: "Building",
                        enabled: true,
                        showCount: false
                    },
                    leases: {
                        label: "Leases",
                        enabled: true,
                        showCount: true,
                        count: this.site.leases?.length || 0
                    },
                    expenses: {
                        label: "Expenses",
                        enabled: true,
                        showCount: true,
                        count: this.site.expense_count
                    },
                    utilization: {
                        label: "Utilization",
                        enabled: true,
                        showCount: false
                    },
                    key_dates: {
                        label: "Key Dates",
                        enabled: true,
                        showCount: true,
                        count: this.site.key_dates?.length || 0
                    },
                    tasks: {
                        label: "Tasks",
                        enabled: true,
                        showCount: true,
                        count: this.site.tasks_count || 0
                    },
                    documents: {
                        label: "Documents",
                        enabled: true,
                        showCount: true,
                        count: this.documents.all.length || 0
                    },
                    contacts: {
                        label: "Contacts",
                        enabled: true,
                        showCount: true,
                        count: this.contacts.all?.length || 0
                    },
                    site_imports: {
                        label: "Site Imports",
                        enabled: true,
                        showCount: false,
                        count: this.site.site_imports?.length || 0
                    },
                    comments: {
                        label: "Comments",
                        enabled: true,
                        showCount: true,
                        count: this.site.comments?.length || 0
                    },
                    dangerZone: {
                        label: "Danger Zone",
                        enabled: true,
                        showCount: false,
                        customClass: this.site.building.is_in_danger_zone ? 'site-detail__tab--danger' : ''
                    },
                    changelog: {
                        label: "Changelog",
                        enabled: this.$gate.isAdmin() && !this.fromReatlas,
                        showCount: true,
                        count: this.site.audits_count || 0
                    }
                }
            },

            quickFactsColumns() {
                return [
                    { 
                        field: 'label', 
                        label: '', 
                        type: 'text',
                        sortable: false,
                        width: '40%'
                    },
                    { 
                        field: 'value', 
                        label: '', 
                        type: 'text',
                        sortable: false,
                        width: '60%'
                    }
                ];
            },

            quickFactsData() {
                return [
                    { 
                        field: 'property_type',
                        label: 'Property Type', 
                        value: this.site.building.type.type 
                    },
                    { 
                        field: 'region',
                        label: 'Region', 
                        value: this.site.building.region?.name || '-' 
                    },
                    { 
                        field: 'leed',
                        label: 'LEED', 
                        value: this.site.building.leed_certification 
                    },
                    { 
                        field: 'lease_own',
                        label: 'Lease/Own', 
                        value: this.site.lease_or_own 
                    },
                    { 
                        field: 'due_in',
                        label: 'Due in', 
                        value: '' // Will be rendered via slot
                    }
                ];
            }
        },

        methods: {
            handleManageSiteAction(action) {
                switch(action) {
                    case 'edit-building':
                        this.gotoEditBuilding();
                        break;
                    case 'manage-leases':
                        this.openManageLeases('lease-info');
                        break;
                    case 'manage-expenses':
                        this.openManageLeases('expenses');
                        break;
                    case 'manage-key-dates':
                        this.openManageLeases('key-dates');
                        break;
                    case 'archive-site':
                        this.archiveSite();
                        break;
                    case 'unarchive-site':
                        this.unarchiveSite();
                        break;
                    case 'delete-site':
                        this.confirmDeleteSite();
                        break;
                }
            },

            showSites() {
                this.$modal.show('site-cards');
            },

            gotoEditBuilding() {
                location.href = `/buildings/${this.site.building.id}/edit`;
            },

            openManageLeases(section) {
                this.leaseSection = section;
                this.$nextTick(() => {
                    this.$modal.show('manage-leases');
                });
            },

            archiveSite() {
                this.site.archived_at = new Date().toISOString().slice(0, 19).replace('T', ' ');
                this.save();
            },

            unarchiveSite() {
                this.site.archived_at = null;
                this.save();
            },

            save() {
                axios.patch('/sites/' + this.site.id, this.site)
                    .then(response => {
                        this.$notify({
                            type: 'success',
                            title: 'Success',
                            text: 'Site Saved'
                        });
                    })
                    .catch(error => {
                        console.error(error);
                    });
            },

            confirmDeleteSite() {
                this.$swal({
                    title: 'Are you sure?',
                    text: "Are you sure you want to delete this site?",
                    type: 'warning',
                    buttonsStyling: false,
                    confirmButtonText: 'Yes - Delete it',
                    confirmButtonClass: 'btn btn-red',
                    showCancelButton: true,
                    cancelButtonClass: 'btn btn-secondary mr-4',
                    reverseButtons: true
                }).then((result) => {
                    if (result.value) {
                        this.deleteSite();
                    }
                });
            },

            deleteSite() {
                axios.delete('/sites/' + this.site.id)
                    .then(response => {
                        this.$notify({
                            type: 'success',
                            title: 'Success',
                            text: 'Site Deleted'
                        });

                        setTimeout(() => {
                            window.location = '/sites';
                        }, 1000);
                    })
                    .catch(error => {
                        this.$notify({
                            type: 'error',
                            title: 'Error',
                            text: error.response.data.message
                        });
                    });
            },

            handleSiteImports(site_imports) {
                this.tabs.site_imports.count = site_imports.length;
                
                if (!this.site?.site_imports) {
                    this.$set(this.site, 'site_imports', []);
                }
                this.site.site_imports = site_imports;
            },

            canEdit() {
                return this.$gate.can('edit', 'site', this.site);
            },

            canDelete() {
                return this.$gate.can('delete', 'site', this.site);
            },

            goBack() {
                window.history.back();
            }
        }
    }
</script>
